import type { ReactNode } from "react";
import { useQuery } from "../data/useQuery.js";
import { useBoundQuery, FillPrompt } from "../data/DwSql.js";
import { ErrorBadge } from "./ErrorBadge.js";
import {
  formatValue,
  formatForName,
  parseFormat,
  parseColumnSpec,
  fmtDelta,
} from "./format.js";
import type { VizProps } from "./registry.js";

/**
 * Big-number metrics. Each column of the first result row is one metric card,
 * labelled by its column name — so the SQL drives the labels:
 *
 *   ```metric
 *   select count(*) as "Total users",
 *          count(*) filter (where created_at >= now() - interval '7 days')
 *            as "This week"
 *   from users
 *   ```
 *
 * A second result row is read as the prior baseline (auto-compare): each card
 * then shows a ▲/▼ delta of row 0 vs row 1. `compare=false` opts out.
 */
export function MetricViz({ source, attrs }: VizProps) {
  const { sql, values, path, status, missing } = useBoundQuery(source.trim());
  const q = useQuery(status === "ready" ? sql : null, {
    // Explicit attr overrides the query's @maxAge pragma; absent → pragma →
    // forever (useQuery sends maxAgeDefault).
    ttl: attrs.maxAge ?? attrs.ttl,
    values,
    path,
  });

  let cards;
  if (status === "blocked") {
    return (
      <div className="dw-embed dw-embed-metrics">
        <FillPrompt names={missing} />
      </div>
    );
  }
  if (q.status === "loading") {
    cards = <Card value="…" />;
  } else if (q.status === "error") {
    cards = <Card value={<ErrorBadge message={q.error.message} />} />;
  } else {
    // Explicit `format=` per column wins; otherwise guess from the column name.
    const fmtOf = parseFormat(attrs.format);
    // Auto-compare: a second row is the prior baseline (row 0 = current), so
    // each card gets a ▲/▼ delta — no flag needed. `compare=false` opts out;
    // `down="cost,churn"` flips good/bad for metrics where lower is better.
    const down = new Set(
      (attrs.down ?? "").split(",").map((s) => s.trim()).filter(Boolean),
    );
    // `vs="This month:vs last month, Today:proj. MTD avg."` names what each
    // delta is measured against, beside the ▲/▼ chip. The text renders
    // verbatim — no "vs " is prepended, so a card can read "proj. MTD avg."
    // as easily as "vs last month". Columns without a delta ignore it.
    const vsLabels = parseColumnSpec(attrs.vs);
    const cur = q.data.rows[0] ?? [];
    const prior = attrs.compare === "false" ? undefined : q.data.rows[1];
    cards = q.data.columns.map((col, i) => {
      const fmt = fmtOf(col.name) ?? formatForName(col.name);
      let deltaPct: number | undefined;
      let bad = false;
      if (prior) {
        const c = Number(cur[i]);
        const p = Number(prior[i]);
        if (Number.isFinite(c) && Number.isFinite(p) && p !== 0) {
          deltaPct = (c - p) / p;
          const up = deltaPct >= 0;
          bad = down.has(col.name) ? up : !up;
        }
      }
      return (
        <Card
          key={i}
          label={col.name}
          value={formatValue(cur[i], fmt)}
          prev={prior ? formatValue(prior[i], fmt) : undefined}
          deltaPct={deltaPct}
          bad={bad}
          vs={vsLabels[col.name]}
        />
      );
    });
  }

  return <div className="dw-embed dw-embed-metrics">{cards}</div>;
}

function Card({
  label,
  value,
  prev,
  deltaPct,
  bad,
  vs,
}: {
  label?: string;
  value: ReactNode;
  prev?: ReactNode;
  deltaPct?: number;
  bad?: boolean;
  vs?: string;
}) {
  return (
    <div className="dw-metric">
      {label && <div className="dw-metric-label">{label}</div>}
      <div className="dw-metric-value">{value}</div>
      {(prev != null || typeof deltaPct === "number") && (
        <div className="dw-metric-compare">
          <div className={`dw-metric-delta ${bad ? "dw-delta-bad" : "dw-delta-good"}`}>
            {prev != null && <span className="dw-metric-prev">{prev}</span>}
            {typeof deltaPct === "number" && (
              <span className="dw-metric-pct">
                {`${deltaPct >= 0 ? "▲" : "▼"} ${fmtDelta(deltaPct)}`}
              </span>
            )}
          </div>
          {vs && <span className="dw-metric-vs">{vs}</span>}
        </div>
      )}
    </div>
  );
}
